import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

// Session ID for tracking user sessions
let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
  return sessionId;
}

export type ActionCategory = 
  | 'database'      // Database operations (insert, update, delete, select)
  | 'edge_function' // Edge function calls
  | 'navigation'    // Page navigation
  | 'interaction'   // User interactions (button clicks, form submissions)
  | 'file'          // File operations (upload, download)
  | 'auth'          // Authentication events
  | 'system';       // System events

export type ActionType =
  // Database actions
  | 'db_insert' | 'db_update' | 'db_delete' | 'db_select'
  // Edge function actions
  | 'edge_function_call'
  // Navigation actions
  | 'page_view' | 'route_change'
  // Interaction actions
  | 'form_submit' | 'button_click' | 'dialog_open' | 'dialog_close'
  // File actions
  | 'file_upload' | 'file_download' | 'file_delete'
  // Auth actions
  | 'login' | 'logout' | 'signup' | 'password_reset'
  // System actions
  | 'error' | 'warning';

export interface ActivityLogEntry {
  action_type: ActionType;
  action_category: ActionCategory;
  entity_type?: string;
  entity_id?: string;
  details?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  duration_ms?: number;
  success?: boolean;
  error_message?: string;
}

/**
 * Logs a user activity to the activity_logs table
 */
/**
 * Logs a user activity to the activity_logs table
 * Uses a fully non-blocking approach to never interfere with main app flow
 */
export function logActivity(entry: ActivityLogEntry): void {
  // Fire-and-forget: don't await, don't block
  setTimeout(async () => {
    try {
      // Get cached session - much faster than getUser() API call
      const { data: { session } } = await supabase.auth.getSession();

      const logData = {
        user_id: session?.user?.id || null,
        session_id: getSessionId(),
        action_type: entry.action_type,
        action_category: entry.action_category,
        entity_type: entry.entity_type || null,
        entity_id: entry.entity_id || null,
        details: (entry.details as Json) || null,
        metadata: (entry.metadata as Json) || null,
        duration_ms: entry.duration_ms || null,
        success: entry.success ?? true,
        error_message: entry.error_message || null,
        ip_address: null,
        user_agent: navigator.userAgent,
      };

      const { error } = await supabase
        .from('activity_logs')
        .insert([logData]);
      
      if (error) {
        console.error('Failed to log activity:', error);
      }
    } catch (err) {
      console.error('Error logging activity:', err);
    }
  }, 0);
}

/**
 * Safely truncates payload data for logging (prevents huge payloads in logs)
 */
function truncatePayload(data: unknown, maxLength: number = 5000): unknown {
  if (data === null || data === undefined) return data;
  
  const str = JSON.stringify(data);
  if (str.length <= maxLength) {
    return data;
  }
  
  // Return truncated indicator with summary
  return {
    _truncated: true,
    _original_size: str.length,
    _preview: str.substring(0, 500) + '...',
  };
}

/**
 * Wraps a database operation to automatically log it with payloads
 */
export async function withDatabaseLogging<T>(
  operation: 'insert' | 'update' | 'delete' | 'select',
  table: string,
  entityId: string | null,
  fn: () => Promise<{ data: T; error: Error | null }>,
  inputPayload?: Record<string, unknown>
): Promise<{ data: T; error: Error | null }> {
  const startTime = Date.now();
  
  try {
    const result = await fn();
    const duration = Date.now() - startTime;

    // Non-blocking log with full payloads
    logActivity({
      action_type: `db_${operation}` as ActionType,
      action_category: 'database',
      entity_type: table,
      entity_id: entityId || undefined,
      details: {
        table,
        operation,
        row_count: Array.isArray(result.data) ? result.data.length : result.data ? 1 : 0,
        request_payload: truncatePayload(inputPayload),
        response_payload: truncatePayload(result.data),
        response_error: result.error ? {
          message: result.error.message,
          name: result.error.name,
        } : null,
      },
      duration_ms: duration,
      success: !result.error,
      error_message: result.error?.message,
    });

    return result;
  } catch (err) {
    const duration = Date.now() - startTime;
    
    // Non-blocking log with request payload and error
    logActivity({
      action_type: `db_${operation}` as ActionType,
      action_category: 'database',
      entity_type: table,
      entity_id: entityId || undefined,
      details: { 
        table, 
        operation,
        request_payload: truncatePayload(inputPayload),
        response_error: {
          message: err instanceof Error ? err.message : 'Unknown error',
          name: err instanceof Error ? err.name : 'Error',
        },
      },
      duration_ms: duration,
      success: false,
      error_message: err instanceof Error ? err.message : 'Unknown error',
    });

    throw err;
  }
}

/**
 * Wraps an edge function call to automatically log it with full payloads
 */
export async function withEdgeFunctionLogging<T>(
  functionName: string,
  body?: Record<string, unknown>,
  fn?: () => Promise<{ data: T; error: Error | null }>
): Promise<{ data: T; error: Error | null }> {
  const startTime = Date.now();

  try {
    const result = fn 
      ? await fn()
      : await supabase.functions.invoke(functionName, { body });
    
    const duration = Date.now() - startTime;

    // Non-blocking log with full request/response payloads
    logActivity({
      action_type: 'edge_function_call',
      action_category: 'edge_function',
      entity_type: 'edge_function',
      entity_id: functionName,
      details: {
        function_name: functionName,
        request_payload: truncatePayload(body),
        response_payload: truncatePayload(result.data),
        response_error: result.error ? {
          message: result.error.message,
          name: result.error.name,
        } : null,
      },
      duration_ms: duration,
      success: !result.error,
      error_message: result.error?.message,
    });

    return result as { data: T; error: Error | null };
  } catch (err) {
    const duration = Date.now() - startTime;

    // Non-blocking log with request payload and error
    logActivity({
      action_type: 'edge_function_call',
      action_category: 'edge_function',
      entity_type: 'edge_function',
      entity_id: functionName,
      details: {
        function_name: functionName,
        request_payload: truncatePayload(body),
        response_error: {
          message: err instanceof Error ? err.message : 'Unknown error',
          name: err instanceof Error ? err.name : 'Error',
          stack: err instanceof Error ? err.stack : undefined,
        },
      },
      duration_ms: duration,
      success: false,
      error_message: err instanceof Error ? err.message : 'Unknown error',
    });

    throw err;
  }
}

/**
 * ActivityLogger provides convenient methods for logging various user activities
 */
export const ActivityLogger = {
  // Navigation
  pageView: (path: string, title?: string) => {
    logActivity({
      action_type: 'page_view',
      action_category: 'navigation',
      entity_type: 'page',
      entity_id: path,
      details: { path, title },
    });
  },

  routeChange: (from: string, to: string) => {
    logActivity({
      action_type: 'route_change',
      action_category: 'navigation',
      details: { from, to },
    });
  },

  // Interactions
  formSubmit: (formName: string, entityType?: string, entityId?: string, details?: Record<string, unknown>) => {
    logActivity({
      action_type: 'form_submit',
      action_category: 'interaction',
      entity_type: entityType || 'form',
      entity_id: entityId || formName,
      details: { form_name: formName, ...details },
    });
  },

  buttonClick: (buttonName: string, context?: string, details?: Record<string, unknown>) => {
    logActivity({
      action_type: 'button_click',
      action_category: 'interaction',
      entity_type: 'button',
      entity_id: buttonName,
      details: { button_name: buttonName, context, ...details },
    });
  },

  dialogOpen: (dialogName: string, details?: Record<string, unknown>) => {
    logActivity({
      action_type: 'dialog_open',
      action_category: 'interaction',
      entity_type: 'dialog',
      entity_id: dialogName,
      details: { dialog_name: dialogName, ...details },
    });
  },

  dialogClose: (dialogName: string, details?: Record<string, unknown>) => {
    logActivity({
      action_type: 'dialog_close',
      action_category: 'interaction',
      entity_type: 'dialog',
      entity_id: dialogName,
      details: { dialog_name: dialogName, ...details },
    });
  },

  // File operations
  fileUpload: (fileName: string, fileType: string, fileSize: number, bucket?: string) => {
    logActivity({
      action_type: 'file_upload',
      action_category: 'file',
      entity_type: 'file',
      entity_id: fileName,
      details: { file_name: fileName, file_type: fileType, file_size: fileSize, bucket },
    });
  },

  fileDownload: (fileName: string, bucket?: string) => {
    logActivity({
      action_type: 'file_download',
      action_category: 'file',
      entity_type: 'file',
      entity_id: fileName,
      details: { file_name: fileName, bucket },
    });
  },

  // Database operations with payload logging
  dbInsert: (table: string, entityId?: string, requestPayload?: Record<string, unknown>, responsePayload?: unknown) => {
    logActivity({
      action_type: 'db_insert',
      action_category: 'database',
      entity_type: table,
      entity_id: entityId,
      details: { table, request_payload: requestPayload, response_payload: responsePayload },
    });
  },

  dbUpdate: (table: string, entityId?: string, requestPayload?: Record<string, unknown>, responsePayload?: unknown) => {
    logActivity({
      action_type: 'db_update',
      action_category: 'database',
      entity_type: table,
      entity_id: entityId,
      details: { table, request_payload: requestPayload, response_payload: responsePayload },
    });
  },

  dbDelete: (table: string, entityId?: string, requestPayload?: Record<string, unknown>, responsePayload?: unknown) => {
    logActivity({
      action_type: 'db_delete',
      action_category: 'database',
      entity_type: table,
      entity_id: entityId,
      details: { table, request_payload: requestPayload, response_payload: responsePayload },
    });
  },

  dbSelect: (table: string, rowCount?: number, requestPayload?: Record<string, unknown>, responsePayload?: unknown) => {
    logActivity({
      action_type: 'db_select',
      action_category: 'database',
      entity_type: table,
      details: { table, row_count: rowCount, request_payload: requestPayload, response_payload: responsePayload },
    });
  },

  // Edge function calls with payload logging
  edgeFunctionCall: (
    functionName: string, 
    success: boolean, 
    durationMs?: number, 
    requestPayload?: Record<string, unknown>,
    responsePayload?: unknown,
    errorDetails?: { message: string; name?: string }
  ) => {
    logActivity({
      action_type: 'edge_function_call',
      action_category: 'edge_function',
      entity_type: 'edge_function',
      entity_id: functionName,
      details: { 
        function_name: functionName,
        request_payload: requestPayload,
        response_payload: responsePayload,
        response_error: errorDetails,
      },
      duration_ms: durationMs,
      success,
      error_message: errorDetails?.message,
    });
  },

  // Error logging
  error: (errorMessage: string, context?: string, details?: Record<string, unknown>) => {
    logActivity({
      action_type: 'error',
      action_category: 'system',
      entity_type: 'error',
      details: { context, ...details },
      success: false,
      error_message: errorMessage,
    });
  },

  // Custom activity logging
  custom: (
    actionType: ActionType,
    actionCategory: ActionCategory,
    entityType?: string,
    entityId?: string,
    details?: Record<string, unknown>
  ) => {
    logActivity({
      action_type: actionType,
      action_category: actionCategory,
      entity_type: entityType,
      entity_id: entityId,
      details,
    });
  },
};

// Reset session on logout
export function resetSession(): void {
  sessionId = null;
}
